import isPlainObject from "lodash/isPlainObject";

type StorageType = "localStorage" | "sessionStorage";

function getStorage(storageType: StorageType): Storage | null {
  try {
    return window[storageType];
  } catch (err) {
    return null;
  }
}

function checkIsSupported(storage: Storage) {
  const key = "__support_test__";

  try {
    storage.setItem(key, key);
    storage.removeItem(key);
    return true;
  } catch (e) {
    return false;
  }
}

function getFromStorage<T = unknown>(key: string, storage: Storage) {
  if (storage.getItem(key)) {
    try {
      return JSON.parse(storage.getItem(key) || "") as T;
    } catch (e) {
      return null;
    }
  }

  return null;
}

function saveToStorage(values: Record<string, any>, storage: Storage) {
  if (!checkIsSupported(storage)) {
    throw new TypeError("Invalid storage instance");
  }

  if (!isPlainObject(values)) {
    throw new TypeError("Storage.save only supports object type argument");
  }

  Object.keys(values).forEach(key => {
    let value = values[key];
    try {
      value = JSON.stringify(value);
    } catch (e) {
      // empty
    }

    storage.setItem(key, value);
  });
}

function removeFromStorage(key: string, storage: Storage) {
  if (!checkIsSupported(storage)) {
    throw new TypeError("Invalid storage instance");
  }

  storage.removeItem(key);
}

// ---------------------------
// Local storage
// ---------------------------

export function getLocalStorage() {
  return getStorage("localStorage");
}

export function getFromLocalStorage<T = unknown>(key: string): T | null {
  const storage = getStorage("localStorage");

  return storage ? getFromStorage<T>(key, localStorage) : null;
}

export function saveToLocalStorage(values: Record<string, any>) {
  const storage = getStorage("localStorage");

  if (storage) {
    saveToStorage(values, localStorage);
  }
}

export function removeFromLocalStorage(key: string) {
  const storage = getStorage("localStorage");

  if (storage) {
    removeFromStorage(key, localStorage);
  }
}

// ---------------------------
// Session storage
// ---------------------------

export function getFromSessionStorage<T = unknown>(key: string): T | null {
  const storage = getStorage("sessionStorage");

  return storage ? getFromStorage<T>(key, sessionStorage) : null;
}

export function saveToSessionStorage(values: Record<string, any>) {
  const storage = getStorage("sessionStorage");

  if (storage) {
    saveToStorage(values, sessionStorage);
  }
}

export function removeFromSessionStorage(key: string) {
  const storage = getStorage("sessionStorage");

  if (storage) {
    removeFromStorage(key, sessionStorage);
  }
}
